import { baseApi as api } from "../../../contexts/endpoints/base_api";
import {
  NullSafePhoneNumberPayload,
  PhoneNumber,
  PhoneNumberPayload,
} from "../../../contexts/endpoints/shared/types";

const injectedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProfile: build.query<GetProfileApiResponse, GetProfileApiArg>({
      query: () => ({ url: `/api/account/profile` }),
      providesTags: ["Profile"],
    }),
    putFirstName: build.mutation<PutFirstNameApiResponse, PutFirstNameApiArg>({
      query: (queryArg) => ({
        url: `/api/account/profile/firstName`,
        method: "PUT",
        body: queryArg.firstNamePayload,
      }),
      invalidatesTags: ["Profile"],
    }),
    putLastName: build.mutation<PutLastNameApiResponse, PutLastNameApiArg>({
      query: (queryArg) => ({
        url: `/api/account/profile/lastName`,
        method: "PUT",
        body: queryArg.lastNamePayload,
      }),
      invalidatesTags: ["Profile"],
    }),
    putJobTitle: build.mutation<PutJobTitleApiResponse, PutJobTitleApiArg>({
      query: (queryArg) => ({
        url: `/api/account/profile/jobTitle`,
        method: "PUT",
        body: queryArg.jobTitlePayload,
      }),
      invalidatesTags: ["Profile"],
    }),
    putCountry: build.mutation<PutCountryApiResponse, PutCountryApiArg>({
      query: (queryArg) => ({
        url: `/api/account/profile/country`,
        method: "PUT",
        body: queryArg.countryCodePayload,
      }),
      invalidatesTags: ["Profile"],
    }),
    putPhoneNumber1: build.mutation<
      PutPhoneNumber1ApiResponse,
      PutPhoneNumber1ApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/profile/phoneNumber`,
        method: "PUT",
        body: queryArg.phoneNumberPayload,
      }),
      invalidatesTags: ["Profile"],
    }),
    putAdditionalEmails: build.mutation<
      PutAdditionalEmailsApiResponse,
      PutAdditionalEmailsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/profile/additionalEmails`,
        method: "PUT",
        body: queryArg.additionalEmailsPayload,
      }),
      invalidatesTags: ["Profile"],
    }),
    putAdditionalEmailsVerify: build.mutation<
      PutAdditionalEmailsVerifyApiResponse,
      PutAdditionalEmailsVerifyApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/profile/additionalEmails/verify`,
        method: "PUT",
        params: {
          email: queryArg.email,
          token: queryArg.token,
        },
      }),
      invalidatesTags: ["Profile"],
    }),
    putResendAdditionalEmailsVerify: build.mutation<
      PutResendAdditionalEmailsVerifyApiResponse,
      PutResendAdditionalEmailsVerifyApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/profile/additionalEmails/resendVerify`,
        method: "PUT",
        params: {
          email: queryArg.email,
        },
      }),
    }),
    putPrimaryEmailAddress: build.mutation<
      PutPrimaryEmailAddressApiResponse,
      PutPrimaryEmailAddressApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/profile/primaryEmailAddress`,
        method: "PUT",
        body: queryArg.emailAddressPayload,
      }),
    }),
    postResetPassword: build.mutation<
      PostResetPasswordApiResponse,
      PostResetPasswordApiArg
    >({
      query: () => ({ url: `/api/account/profile/password`, method: "POST" }),
      invalidatesTags: ["Profile"],
    }),
    deleteAccount: build.mutation<
      DeleteAccountApiResponse,
      DeleteAccountApiArg
    >({
      query: () => ({ url: `/api/account/profile`, method: "DELETE" }),
    }),
    // Public profile share settings
    putSharePrimaryEmail: build.mutation<
      PutSharePrimaryEmailApiResponse,
      PutSharePrimaryEmailApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/publicProfile/sharePrimaryEmailAddress`,
        method: "PUT",
        body: queryArg.sharePrimaryEmailAddressPayload,
      }),
    }),
    putSharePhoneNumber: build.mutation<
      PutSharePhoneNumberApiResponse,
      PutSharePhoneNumberApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/publicProfile/sharePhoneNumber`,
        method: "PUT",
        body: queryArg.sharePhoneNumberPayload,
      }),
    }),
    putShareJobTitle: build.mutation<
      PutShareJobTitleApiResponse,
      PutShareJobTitleApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/publicProfile/shareJobTitle`,
        method: "PUT",
        body: queryArg.shareJobTitlePayload,
      }),
    }),
    putShareCountry: build.mutation<
      PutShareCountryApiResponse,
      PutShareCountryApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/publicProfile/shareCountry`,
        method: "PUT",
        body: queryArg.shareCountryPayload,
      }),
    }),
    putShareCertifications: build.mutation<
      PutShareCertificationsApiResponse,
      PutShareCertificationsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/publicProfile/shareCertifications`,
        method: "PUT",
        body: queryArg.shareCertificationsPayload,
      }),
    }),
    putShareCredentials: build.mutation<
      PutShareCredentialsApiResponse,
      PutShareCredentialsApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/publicProfile/shareCredentials`,
        method: "PUT",
        body: queryArg.shareCredentialsPayload,
      }),
    }),
    putElectiveStudies: build.mutation<
      PutElectiveStudiesApiResponse,
      PutElectiveStudiesApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/publicProfile/shareElectiveStudies`,
        method: "PUT",
        body: queryArg.shareElectiveStudiesPayload,
      }),
    }),
    putIsEnabled: build.mutation<PutIsEnabledApiResponse, PutIsEnabledApiArg>({
      query: (queryArg) => ({
        url: `/api/account/publicProfile/isEnabled`,
        method: "PUT",
        body: queryArg.isEnabledPayload,
      }),
      invalidatesTags: ["Profile"],
    }),
  }),
  overrideExisting: false,
});

export type GetProfileApiResponse = /** status 200 OK */ ProfileDetails;
export type GetProfileApiArg = void;
export type PutFirstNameApiResponse = /** status 200 OK */ FirstNamePayload;
export type PutFirstNameApiArg = {
  firstNamePayload: FirstNamePayload;
};
export type PutLastNameApiResponse = /** status 200 OK */ LastNamePayload;
export type PutLastNameApiArg = {
  lastNamePayload: LastNamePayload;
};
export type PutJobTitleApiResponse = /** status 200 OK */ JobTitlePayload;
export type PutJobTitleApiArg = {
  jobTitlePayload: JobTitlePayload;
};
export type PutCountryApiResponse = /** status 200 OK */ CountryCodePayload;
export type PutCountryApiArg = {
  countryCodePayload: CountryCodePayload;
};
export type PutPhoneNumber1ApiResponse =
  /** status 200 OK */ PhoneNumberPayload;
export type PutPhoneNumber1ApiArg = {
  phoneNumberPayload: PhoneNumberPayload;
};
export type PutAdditionalEmailsApiResponse =
  /** status 200 OK */ AdditionalEmailsPayload;
export type PutAdditionalEmailsApiArg = {
  additionalEmailsPayload: AdditionalEmailsPayload;
};
export type PutAdditionalEmailsVerifyApiResponse = /** status 200 OK */ object;
export type PutAdditionalEmailsVerifyApiArg = {
  /** additional e-mail address to verify */
  email: string;
  token: string;
};
export type PutResendAdditionalEmailsVerifyApiResponse =
  /** status 200 OK */ object;
export type PutResendAdditionalEmailsVerifyApiArg = {
  /** additional e-mail address to resend a verification email to */
  email: string;
};
export type PutPrimaryEmailAddressApiResponse =
  /** status 200 OK */ EmailAddressPayload;
export type PutPrimaryEmailAddressApiArg = {
  emailAddressPayload: EmailAddressPayload;
};
export type PostResetPasswordApiResponse = /** status 200 OK */ object;
export type PostResetPasswordApiArg = void;
export type DeleteAccountApiResponse = /** status 200 OK */ object;
export type DeleteAccountApiArg = void;
export type PutSharePrimaryEmailApiResponse =
  /** status 200 OK */ SharePrimaryEmailAddressPayload;
export type PutSharePrimaryEmailApiArg = {
  sharePrimaryEmailAddressPayload: SharePrimaryEmailAddressPayload;
};
export type PutSharePhoneNumberApiResponse =
  /** status 200 OK */ SharePhoneNumberPayload;
export type PutSharePhoneNumberApiArg = {
  sharePhoneNumberPayload: SharePhoneNumberPayload;
};
export type PutShareJobTitleApiResponse =
  /** status 200 OK */ ShareJobTitlePayload;
export type PutShareJobTitleApiArg = {
  shareJobTitlePayload: ShareJobTitlePayload;
};
export type PutShareCountryApiResponse =
  /** status 200 OK */ ShareCountryPayload;
export type PutShareCountryApiArg = {
  shareCountryPayload: ShareCountryPayload;
};
export type PutShareCertificationsApiResponse =
  /** status 200 OK */ ShareCertificationsPayload;
export type PutShareCertificationsApiArg = {
  shareCertificationsPayload: ShareCertificationsPayload;
};
export type PutShareCredentialsApiResponse =
  /** status 200 OK */ ShareCredentialsPayload;
export type PutShareCredentialsApiArg = {
  shareCredentialsPayload: ShareCredentialsPayload;
};
export type PutElectiveStudiesApiResponse =
  /** status 200 OK */ ShareElectiveStudiesPayload;
export type PutElectiveStudiesApiArg = {
  shareElectiveStudiesPayload: ShareElectiveStudiesPayload;
};
export type PutIsEnabledApiResponse = /** status 200 OK */ IsEnabledPayload;
export type PutIsEnabledApiArg = {
  isEnabledPayload: IsEnabledPayload;
};

export type FirstNamePayload = {
  firstName: string;
};
export type LastNamePayload = {
  lastName: string;
};
export type JobTitlePayload = {
  jobTitle: string;
};
export type CountryCodePayload = {
  country?: string;
};
export type EmailAddressPayload = {
  emailAddress: string;
};
export type AdditionalEmailsPayload = {
  additionalEmails: string[];
};
export type SharePrimaryEmailAddressPayload = {
  sharePrimaryEmailAddress: boolean;
};
export type SharePhoneNumberPayload = {
  sharePhoneNumber: boolean;
};
export type ShareJobTitlePayload = {
  shareJobTitle: boolean;
};
export type ShareCountryPayload = {
  shareCountry: boolean;
};
export type ShareCertificationsPayload = {
  shareCertifications: boolean;
};
export type ShareCredentialsPayload = {
  shareCredentials: boolean;
};
export type ShareElectiveStudiesPayload = {
  shareElectiveStudies: boolean;
};
export type IsEnabledPayload = {
  isEnabled: boolean;
};
export type StatefulEmailAddress = {
  email: string;
  status: "UNVERIFIED" | "PENDING" | "VERIFIED";
};
export type PublicProfileSettings = {
  isEnabled: boolean;
  publicId: string;
  sharePrimaryEmailAddress: boolean;
  sharePhoneNumber: boolean;
  shareJobTitle: boolean;
  shareCountry: boolean;
  shareCertifications: boolean;
  shareCredentials: boolean;
  shareElectiveStudies: boolean;
};
export type ProfileDetails = {
  id: string;
  avatarUrl: string;
  firstName: string;
  lastName: string;
  phoneNumber?: PhoneNumber;
  jobTitle: string;
  country: string;
  primaryEmailAddress: string;
  additionalEmails: StatefulEmailAddress[];
  publicProfile: PublicProfileSettings;
};

export type { NullSafePhoneNumberPayload, PhoneNumber, PhoneNumberPayload };

export const {
  useGetProfileQuery,
  usePutFirstNameMutation,
  usePutLastNameMutation,
  usePutJobTitleMutation,
  usePutCountryMutation,
  usePutPhoneNumber1Mutation,
  usePutAdditionalEmailsMutation,
  usePutAdditionalEmailsVerifyMutation,
  usePutResendAdditionalEmailsVerifyMutation,
  usePutPrimaryEmailAddressMutation,
  usePostResetPasswordMutation,
  useDeleteAccountMutation,
  usePutSharePrimaryEmailMutation,
  usePutSharePhoneNumberMutation,
  usePutShareJobTitleMutation,
  usePutShareCountryMutation,
  usePutShareCertificationsMutation,
  usePutShareCredentialsMutation,
  usePutElectiveStudiesMutation,
  usePutIsEnabledMutation,
} = injectedApi;
